import type { History } from 'history';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as serviceWorker from '../../serviceWorker';

// Reload exactly once; controllerchange can fire more than once. Module-level so
// the guard survives across every tab-scoped listener in this provider.
let refreshing = false;

const POLL_INTERVAL_MS = 60 * 60 * 1000; // hourly update() poll for all-day tabs
const VISIBLE_UPDATE_THROTTLE_MS = 10 * 60 * 1000; // at most one update() per visible burst
const REPROMPT_DELAY_MS = 30 * 60 * 1000; // re-show snackbar if a deferred update never applied
const CONTROLLER_CHANGE_TIMEOUT_MS = 10 * 1000; // hard reload if SKIP_WAITING is ignored

export interface ServiceWorkerContextValue {
  isUpdateAvailable: boolean;
  isUpdating: boolean;
  applyUpdate: () => void;
  deferUpdate: () => void;
}

const ServiceWorkerContext = createContext<ServiceWorkerContextValue | null>(
  null,
);

interface ServiceWorkerProviderProps {
  children: React.ReactNode;
  // Passed from index.tsx (not imported) to avoid an App -> hook -> App cycle.
  history?: History;
}

export const ServiceWorkerProvider = ({
  children,
  history,
}: ServiceWorkerProviderProps) => {
  const [isUpdateAvailable, setUpdateAvailable] = useState(false);
  const [isUpdating, setUpdating] = useState(false);

  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const initiatedRef = useRef(false); // this tab kicked off the update
  const deferredRef = useRef(false); // user chose "Later" in this tab
  const lastVisibleUpdateRef = useRef(0);

  const reloadOnce = useCallback(() => {
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  }, []);

  // Runs `action` at the first safe moment: next route change or when the tab is
  // hidden (a hidden reload is invisible to the user). Returns a canceller.
  const runAtSafeMoment = useCallback(
    (action: () => void) => {
      let done = false;
      const onVisibility = () => {
        if (document.visibilityState === 'hidden') run();
      };
      let unlisten: (() => void) | undefined;
      const cleanup = () => {
        document.removeEventListener('visibilitychange', onVisibility);
        unlisten?.();
      };
      const run = () => {
        if (done) return;
        done = true;
        cleanup();
        action();
      };
      document.addEventListener('visibilitychange', onVisibility);
      if (history) {
        unlisten = history.listen(() => run());
      }
      return cleanup;
    },
    [history],
  );

  const applyUpdate = useCallback(() => {
    // Re-read waiting at call time; state captured earlier may be stale after a
    // second deploy while deferred.
    const waiting = registrationRef.current?.waiting;
    initiatedRef.current = true;
    setUpdating(true);

    if (!waiting) {
      // Never leave the user on a dead button; just reload what is current.
      window.location.reload();
      return;
    }

    waiting.postMessage({ type: 'SKIP_WAITING' });

    // Recovery: if the worker ignores SKIP_WAITING, force a reload.
    window.setTimeout(() => {
      if (!refreshing) window.location.reload();
    }, CONTROLLER_CHANGE_TIMEOUT_MS);
  }, []);

  const deferUpdate = useCallback(() => {
    if (deferredRef.current) return;
    deferredRef.current = true;
    setUpdateAvailable(false);

    let repromptTimer = 0;
    const cancelSafe = runAtSafeMoment(() => {
      window.clearTimeout(repromptTimer);
      applyUpdate();
    });
    repromptTimer = window.setTimeout(() => {
      cancelSafe();
      deferredRef.current = false;
      setUpdateAvailable(true);
    }, REPROMPT_DELAY_MS);
  }, [runAtSafeMoment, applyUpdate]);

  // Register (single owner) and react to worker activation.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const onControllerChange = () => {
      if (refreshing) return;
      if (initiatedRef.current || document.visibilityState === 'hidden') {
        reloadOnce();
      } else {
        // Another tab activated the update; don't yank this tab mid-work.
        runAtSafeMoment(reloadOnce);
      }
    };

    navigator.serviceWorker.addEventListener(
      'controllerchange',
      onControllerChange,
    );

    serviceWorker
      .register({
        onUpdate: registration => {
          registrationRef.current = registration;
          setUpdateAvailable(true);
        },
      })
      .then(registration => {
        if (registration) registrationRef.current = registration;
      });

    return () => {
      navigator.serviceWorker.removeEventListener(
        'controllerchange',
        onControllerChange,
      );
    };
  }, [reloadOnce, runAtSafeMoment]);

  // Poll for a newer worker: hourly, plus (throttled) whenever the tab becomes
  // visible again. update() rejections (offline) are swallowed and retried next tick.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const poll = () => registrationRef.current?.update().catch(() => {});
    const interval = window.setInterval(poll, POLL_INTERVAL_MS);

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastVisibleUpdateRef.current < VISIBLE_UPDATE_THROTTLE_MS) return;
      lastVisibleUpdateRef.current = now;
      poll();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const value = useMemo<ServiceWorkerContextValue>(
    () => ({ isUpdateAvailable, isUpdating, applyUpdate, deferUpdate }),
    [isUpdateAvailable, isUpdating, applyUpdate, deferUpdate],
  );

  return (
    <ServiceWorkerContext.Provider value={value}>
      {children}
    </ServiceWorkerContext.Provider>
  );
};

export const useServiceWorker = (): ServiceWorkerContextValue => {
  const context = useContext(ServiceWorkerContext);
  if (!context) {
    throw new Error(
      'useServiceWorker must be used within a ServiceWorkerProvider',
    );
  }
  return context;
};
